import React, {
  createContext,
  useContext,
  useState,
  useMemo,
  type ReactNode, useCallback, useEffect,
} from 'react';
import { themes } from '../themes';
import { Theme, ThemeContextProps, ThemeName } from '../themes/types';

const ThemeContext = createContext<ThemeContextProps>({
  themeName: 'default',
  setTheme: () => undefined,
  theme: themes.default,
  handleTheme: () => undefined,
});

// Merge default theme with current theme, overriding default values
// Avoid a Theme not having a value for a property that is defined in the default theme
const mergeThemes = (defaultTheme: Theme, currentTheme: Theme): Theme => ({ ...defaultTheme, ...currentTheme });

export const ThemeProvider = ({ children }: { children: ReactNode }) => {
  const [themeName, setTheme] = useState<ThemeName>('default');

  const theme = useMemo(() => mergeThemes(themes.default, themes[themeName]), [themeName]);

  useEffect(() => {
    const url = window.location.href;
    //const url = 'https://galp.elecctro.com';
    const regex = /^(?:https?:\/\/)?(.*?)\.elecctro\.com/;
    const match = url.match(regex);

    const companyTag: ThemeName = 'default';
    setTheme(companyTag);
    switch (match && match[1]) {
      case 'iberdrola':
        setTheme('iberdrola');
        break;
      case 'brightcity':
        setTheme('bright');
        break;
      case 'sogenave':
        setTheme('sogenave');
        break;
      case 'edp':
        setTheme('EDP');
        break;
      case 'helexia':
        setTheme('helexia');
        break;
      case 'galp':
        setTheme('galp');
        break;
      default:
        setTheme('default');
        break;
    }
  }, []);

  const handleTheme = useCallback((tag: string) => {
    switch (tag) {
      case 'FCT':
        setTheme('iberdrola');
        break;
      case 'BRC':
        setTheme('bright');
        break;
      case 'SERDIAL':
        setTheme('sogenave');
        break;
      case 'ED1':
        setTheme('EDP');
        break;
      case 'BL1':
        setTheme('helexia');
        break;
      case 'galp': // server company.tag (case-sensitive — confirm exact string with backend/ops)
        setTheme('galp');
        break;
      default:
        setTheme('default');
        break;
    }
  }, [setTheme]);

  const value = useMemo(
    () => ({
      themeName,
      setTheme,
      theme,
      handleTheme,
    }),
    [themeName, theme, handleTheme],
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);
